import React, { useState, useEffect } from 'react';
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import { Habit } from '../../types';
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { useDeleteHabit } from '../../hooks/useHabits';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";

// Define simple type for form data
type HabitFormData = {
  name: string;
  description: string | null;
  frequency: 'DAILY' | 'WEEKLY';
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (habitData: HabitFormData, habitId?: number) => void;
  initialData?: Habit | null;
  isLoading?: boolean;
}

export default function AddEditHabitModal({ isOpen, onClose, onSave, initialData, isLoading }: Props) {
  const isEditing = !!initialData;
  const { mutate: deleteHabitMutate, isPending: isDeleting } = useDeleteHabit();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const form = useForm<HabitFormData>({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || null,
      frequency: initialData?.frequency === 'WEEKLY' ? 'WEEKLY' : 'DAILY',
    },
  });

  // Reset form when initialData changes
  useEffect(() => {
    form.reset({
      name: initialData?.name || "",
      description: initialData?.description || null,
      frequency: initialData?.frequency === 'WEEKLY' ? 'WEEKLY' : 'DAILY',
    });
  }, [initialData, form]);

  const onSubmit = (data: HabitFormData) => {
    onSave(data, initialData?.id);
  };

  const handleDelete = () => {
    if (initialData?.id) {
      deleteHabitMutate(initialData.id, {
        onSuccess: () => {
          console.log("Habit deleted successfully");
          setIsDeleteDialogOpen(false);
          onClose();
        },
        onError: (error: Error) => {
          console.error("Error deleting habit:", error);
          // TODO: Show error message to user
          setIsDeleteDialogOpen(false);
        }
      });
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Habit' : 'Add New Habit'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the details of your habit.' : 'Create a new habit to track.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">Name</Label>
            <Input
              id="name"
              placeholder="e.g., Daily Meditation"
              className="col-span-3"
              {...form.register("name", {
                required: "Habit name is required.",
                maxLength: { value: 100, message: "Name cannot exceed 100 characters." }
              })}
            />
            {form.formState.errors.name && (
              <p className="col-span-3 col-start-2 text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">Description</Label>
            <Textarea
              id="description"
              placeholder="(Optional) Add details..."
              className="col-span-3"
              {...form.register("description")}
            />
          </div>
          <Controller
            control={form.control}
            name="frequency"
            render={({ field }: { field: ControllerRenderProps<HabitFormData, 'frequency'> }) => (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Frequency</Label>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="col-span-3 flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="DAILY" id="freq-daily" />
                    <Label htmlFor="freq-daily" className="font-normal">Daily</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="WEEKLY" id="freq-weekly" />
                    <Label htmlFor="freq-weekly" className="font-normal">Weekly</Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          />
        </form>
        <DialogFooter className="sm:justify-between">
          <div>
            {isEditing && (
              <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" disabled={isDeleting}>
                    {isDeleting ? "Deleting..." : "Delete Habit"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the habit
                      "{initialData?.name}" and all its completion history.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={handleCancelDelete} disabled={isDeleting}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                      {isDeleting ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" onClick={form.handleSubmit(onSubmit)} disabled={isLoading}>
              {isLoading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Habit')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 